import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchAdminClinics } from "@services/clinicUsers";
import { isAdmin as checkIsAdmin } from "@utils/access";
import {
  clearSelectedClinic,
  readSelectedClinic,
  storeSelectedClinic,
} from "@utils/clinicScopeStorage";

export { clearSelectedClinic };

const ClinicContext = createContext(null);

/** Every shape the backend has sent for GET /clinic/clinics. */
function rowsOf(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.clinics)) return res.clinics;
  if (Array.isArray(res)) return res;
  return [];
}

/**
 * The one owner of "which clinic is the admin looking at". Admins get the
 * clinic list once per session, a selection that survives navigation and
 * reload, and a setter the picker calls. A clinic user gets null and an
 * empty list: they never choose, the backend scopes them by their token.
 */
export function ClinicScopeProvider({ children }) {
  const isAdmin = useMemo(() => checkIsAdmin(), []);
  const [clinicId, setClinicIdState] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(isAdmin);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = rowsOf(await fetchAdminClinics());
        if (cancelled) return;
        setClinics(rows);
        // A remembered id only counts when it still names a clinic; a stale
        // one must not select a clinic that no longer exists.
        const remembered = readSelectedClinic();
        const kept = rows.find((c) => c.id === remembered);
        const first = rows.find((c) => c.is_active !== false);
        const chosen = (kept || first)?.id ?? null;
        setClinicIdState(chosen);
        storeSelectedClinic(chosen);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || "Failed to load clinics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  const setClinicId = useCallback((id) => {
    const next = id || null;
    setClinicIdState(next);
    storeSelectedClinic(next);
  }, []);

  const value = useMemo(
    () => ({ clinicId, setClinicId, clinics, loading, error, isAdmin }),
    [clinicId, setClinicId, clinics, loading, error, isAdmin]
  );

  return <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>;
}

export function useClinicScope() {
  const ctx = useContext(ClinicContext);
  if (!ctx) throw new Error("useClinicScope must be used within a ClinicScopeProvider");
  return ctx;
}
